/**
 * JSON-LD structured data for SEO.
 * Injected into <head> at runtime; baked into static HTML after prerendering.
 */

// ── FAQPage schema ──────────────────────────────────────────────────────────
const faqEntries = [
  // Pricing & Subscriptions
  {
    q: "Is knooth free to use?",
    a: "knooth is available on the Mac App Store with a free trial. After the trial, a paid subscription is required to continue using the app.",
  },
  {
    q: "What plans are available?",
    a: "knooth offers monthly and yearly subscription plans: $9.99/month or $59.99/year. The yearly plan saves you 50% compared to monthly billing.",
  },
  {
    q: "Can I switch between monthly and yearly plans later?",
    a: "Yes. You can switch between monthly and yearly subscriptions at any time through your Apple ID subscription settings.",
  },
  {
    q: "Are updates included in the paid plans?",
    a: "Yes. All paid plans include ongoing updates, improvements, and new features.",
  },

  // Licensing & Devices
  {
    q: "Can I use knooth on multiple Macs?",
    a: "Yes. One license allows you to install knooth on any Mac using the same Apple ID you purchased with, in accordance with Apple's App Store licensing rules.",
  },
  {
    q: "Is knooth intended for individual use?",
    a: "Yes. knooth is currently designed for individual use and personal or professional workflows.",
  },

  // Privacy & Data
  {
    q: "Does knooth upload or store my data?",
    a: "No. knooth does not upload, store, or process your recordings or assets on any external servers. All files are created and saved locally on your device.",
  },
  {
    q: "Who owns the content created with knooth?",
    a: "You do. All recordings, edits, and exported videos are fully owned by you. knooth does not claim ownership over any content you create.",
  },
  {
    q: "Does knooth collect analytics or tracking data?",
    a: "Yes. knooth collects limited, anonymous usage analytics to help us understand how the app is used and to improve performance and features.",
  },

  // AI in knooth
  {
    q: "Does knooth use AI?",
    a: "Yes. knooth includes optional AI-powered features to help with tasks like generating captions and cleaning up speech disfluencies (such as filler words). AI-generated output may not always be accurate, complete, or error-free.",
  },
  {
    q: "Is my data sent to the cloud for AI processing?",
    a: "No. All AI processing in knooth happens entirely on your device. Your audio, video, and text never leave your device during AI operations.",
  },
  {
    q: "Does knooth use third-party AI services?",
    a: "No. knooth does not use cloud-based AI services, external APIs, or third-party inference servers. Everything runs locally inside the app.",
  },
  {
    q: "What languages does knooth support?",
    a: "knooth's AI features support 25+ languages including English, French, German, Spanish, and many more.",
  },
  {
    q: "Does knooth collect or store AI-generated data?",
    a: "No. AI-generated captions and edits are stored locally in your project files. knooth does not collect, store, or transmit AI inputs or outputs. We do not use your content to train any models.",
  },
  {
    q: "Will AI features work offline?",
    a: "Yes. Since all AI processing is on-device, AI features work without an internet connection, subject to your device's performance capabilities.",
  },
  {
    q: "Why might AI performance vary between devices?",
    a: "knooth's AI runs locally on your Mac, so performance depends on your hardware. Performance may vary on older or lower-spec devices.",
  },
  {
    q: "Does AI affect my privacy?",
    a: "No. knooth's AI runs entirely on your device. Your content stays on your Mac and is never uploaded or processed in the cloud. Anonymous usage and diagnostics data may be collected to improve the app, but no personal data or content is ever collected.",
  },
  {
    q: "Is AI used for decision-making or profiling?",
    a: "No. AI in knooth is used solely as a creative and productivity tool. It does not profile users or make automated decisions about you.",
  },

  // App Store & Payments
  {
    q: "How are subscriptions managed?",
    a: "All payments, subscriptions, renewals, and cancellations are handled through Apple's App Store. knooth does not process payments directly.",
  },
  {
    q: "Can I cancel my subscription anytime?",
    a: "Yes. Subscriptions can be managed or canceled at any time through your Apple ID subscription settings.",
  },
  {
    q: "Does knooth require an internet connection?",
    a: "No internet connection is required to record, edit, or export videos. An internet connection may be needed for downloading updates or managing subscriptions through the App Store.",
  },
];

function buildFaqPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntries.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: a,
      },
    })),
  };
}

// ── SoftwareApplication schema ──────────────────────────────────────────────
function buildSoftwareApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "knooth",
    description:
      "Record your screen, highlight key moments, and export a clean video quickly — with built-in editing, timelines, animations, and media layering.",
    url: "https://www.knooth.com",
    downloadUrl: "https://apps.apple.com/app/knooth/id6756659300?mt=12",
    installUrl: "https://apps.apple.com/app/knooth/id6756659300?mt=12",
    applicationCategory: "MultimediaApplication",
    operatingSystem: "macOS 15.6+",
    offers: [
      {
        "@type": "Offer",
        price: "9.99",
        priceCurrency: "USD",
        name: "Explorer (Monthly)",
        description: "Pay as you go. Cancel anytime.",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "9.99",
          priceCurrency: "USD",
          billingDuration: "P1M",
        },
      },
      {
        "@type": "Offer",
        price: "60",
        priceCurrency: "USD",
        name: "Professional (Yearly)",
        description: "Billed yearly — 50% savings.",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "60",
          priceCurrency: "USD",
          billingDuration: "P1Y",
        },
      },
    ],
    featureList: [
      "Screen recording",
      "Built-in video editing",
      "Timeline editor",
      "Auto Zoom Cursor",
      "On-device AI captions",
      "Filler word removal",
      "Background masking",
      "Studio-ready audio",
      "Multiple export aspect ratios",
      "Privacy-first — all data stays on device",
    ],
    screenshot: "https://www.knooth.com/assets/og-cover.png",
    image: "https://www.knooth.com/assets/og-cover.png",
  };
}

// ── Organization schema ────────────────────────────────────────────────────
function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "knooth",
    url: "https://www.knooth.com",
    logo: "https://www.knooth.com/assets/logo.png",
    sameAs: ["https://x.com/knooth"],
  };
}

// ── Article schema for guide pages ─────────────────────────────────────────
function buildArticleSchema({ title, description, slug }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url: `https://www.knooth.com/guide/${slug}`,
    image: "https://www.knooth.com/assets/og-cover.png",
    author: {
      "@type": "Organization",
      name: "knooth",
      url: "https://www.knooth.com",
    },
    publisher: {
      "@type": "Organization",
      name: "knooth",
      logo: {
        "@type": "ImageObject",
        url: "https://www.knooth.com/assets/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.knooth.com/guide/${slug}`,
    },
  };
}

// ── BreadcrumbList schema for guide pages ──────────────────────────────────
function buildBreadcrumbSchema({ title, slug }) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.knooth.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Guide",
        item: "https://www.knooth.com/guide",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: title,
        item: `https://www.knooth.com/guide/${slug}`,
      },
    ],
  };
}

// ── Injection helper ────────────────────────────────────────────────────────
const SCRIPT_ID_FAQ = "ld-json-faq";
const SCRIPT_ID_APP = "ld-json-app";
const SCRIPT_ID_ORG = "ld-json-org";
const SCRIPT_ID_ARTICLE = "ld-json-article";
const SCRIPT_ID_BREADCRUMB = "ld-json-breadcrumb";

function upsertJsonLd(id, data) {
  if (typeof document === "undefined") return;

  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.remove();
}

/**
 * Inject all structured data for the homepage.
 * Call once on the "/" route.
 */
export function injectHomepageStructuredData() {
  upsertJsonLd(SCRIPT_ID_FAQ, buildFaqPageSchema());
  upsertJsonLd(SCRIPT_ID_APP, buildSoftwareApplicationSchema());
  upsertJsonLd(SCRIPT_ID_ORG, buildOrganizationSchema());
}

/**
 * Remove homepage-specific structured data (when navigating away).
 */
export function removeHomepageStructuredData() {
  removeJsonLd(SCRIPT_ID_FAQ);
  removeJsonLd(SCRIPT_ID_APP);
}

/**
 * Inject structured data for a guide article page.
 * @param {{ title: string, description: string, slug: string }} guide
 */
export function injectGuideStructuredData(guide) {
  upsertJsonLd(SCRIPT_ID_ARTICLE, buildArticleSchema(guide));
  upsertJsonLd(SCRIPT_ID_BREADCRUMB, buildBreadcrumbSchema(guide));
  upsertJsonLd(SCRIPT_ID_ORG, buildOrganizationSchema());
}

/**
 * Remove guide-specific structured data (when navigating away).
 */
export function removeGuideStructuredData() {
  removeJsonLd(SCRIPT_ID_ARTICLE);
  removeJsonLd(SCRIPT_ID_BREADCRUMB);
}
